const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;
const TOKEN_KEY = 'idolaris_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Turn a backend-relative file path (/api/files/x) into an absolute URL
export const fileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE}${path}`;
};

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail;
    try { detail = (await res.json()).detail; } catch { detail = res.statusText; }
    const err = new Error(typeof detail === 'string' ? detail : (detail?.message || 'Request failed'));
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  if (res.status === 204) return null;
  return res.json();
}

export const api = {
  // auth
  register: (d) => request('POST', '/auth/register', d),
  login: (d) => request('POST', '/auth/login', d),
  me: () => request('GET', '/auth/me'),
  forgotPassword: (d) => request('POST', '/auth/forgot', d),
  resetPassword: (d) => request('POST', '/auth/reset', d),
  // entitlements / license / byok
  getEntitlements: () => request('GET', '/entitlements'),
  publicConfig: () => request('GET', '/public/config'),
  // The buyer's own provider keys, not the operator's admin panel.
  myKeys: () => request('GET', '/me/keys'),
  saveMyKeys: (d) => request('POST', '/me/keys', d),
  testMyKey: (provider) => request('POST', '/me/test-key', { provider }),
  finishOnboarding: () => request('POST', '/me/onboarding-done'),
  adminGetConfig: () => request('GET', '/admin/config'),
  // Que falta para poder cobrar, leido del estado y no de una lista.
  adminReadiness: () => request('GET', '/admin/readiness'),
  adminSaveConfig: (d) => request('POST', '/admin/config', d),
  adminTestKey: (provider) => request('POST', '/admin/test-key', { provider }),
  // saved prompts, scoped to the signed-in creator
  activeProviders: () => request('GET', '/providers/active'),
  voiceCatalogue: () => request('GET', '/voices'),
  productCategories: () => request('GET', '/product-categories'),
  draftMotionScript: (d) => request('POST', '/video/draft-script', d),
  checkScript: (d) => request('POST', '/video/check-script', d),
  planSequence: (d) => request('POST', '/video/plan-sequence', d),
  softenScript: (d) => request('POST', '/video/soften-script', d),
  // Ready made UGC scenarios. The graph is built on the server so the picker,
  // the script and the video settings all read one definition.
  listUgcTemplates: () => request('GET', '/ugc-templates'),
  startUgcTemplate: (d) => request('POST', '/ugc-templates/start', d),
  listPhotoRecipes: () => request('GET', '/photo-recipes'),
  // Local OpenCV comic page restoration — no credits, no provider.
  comicProcess: (d) => request('POST', '/comic/process', d),
  // Multipart, not JSON: the scan goes up at its original resolution instead
  // of through the 1280px asset resize, so print restoration keeps its pixels.
  comicProcessUpload: async (file, mode, printSize) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('mode', mode);
    fd.append('print_size', printSize ? 'true' : 'false');
    const headers = {};
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const res = await fetch(`${API}/comic/process-upload`, { method: 'POST', headers, body: fd });
    if (!res.ok) {
      let detail;
      try { detail = (await res.json()).detail; } catch { detail = res.statusText; }
      throw new Error(typeof detail === 'string' ? detail : 'Upload failed');
    }
    return res.json();
  },
  improvePrompt: (d) => request('POST', '/prompt/improve', d),
  upscaleGeneration: (id, d) => request('POST', `/generations/${id}/upscale`, d),
  // El texto del anuncio, leido de la fotografia terminada.
  listingMarketplaces: () => request('GET', '/listing-marketplaces'),
  listingCopy: (d) => request('POST', '/listing-copy', d),
  listListings: (generationId) => request('GET', generationId
    ? `/listings?generationId=${encodeURIComponent(generationId)}` : '/listings'),
  startPhotoRecipe: (d) => request('POST', '/photo-recipes/start', d),
  listPresets: () => request('GET', '/presets'),
  createPreset: (d) => request('POST', '/presets', d),
  deletePreset: (id) => request('DELETE', `/presets/${id}`),
  adminMintLicense: (d) => request('POST', '/admin/license/mint', d),
  adminLicenseEvents: () => request('GET', '/admin/license-events'),
  adminSalesSummary: () => request('GET', '/admin/sales-summary'),
  // assets
  listAssets: (type) => request('GET', `/assets${type ? `?type=${type}` : ''}`),
  createAsset: (d) => request('POST', '/assets', d),
  screenshotWebsite: (d) => request('POST', '/website/screenshot', d),
  patchAsset: (id, d) => request('PATCH', `/assets/${id}`, d),
  deleteAsset: (id, force) => request('DELETE', `/assets/${id}${force ? '?force=1' : ''}`),
  // projects & workflows
  listProjects: () => request('GET', '/projects'),
  createProject: (d) => request('POST', '/projects', d),
  listWorkflows: (pid) => request('GET', `/workflows${pid ? `?projectId=${pid}` : ''}`),
  createWorkflow: (d) => request('POST', '/workflows', d),
  getWorkflow: (id) => request('GET', `/workflows/${id}`),
  updateWorkflow: (id, d) => request('PUT', `/workflows/${id}`, d),
  cloneWorkflow: (id) => request('POST', `/workflows/${id}/clone`),
  // The recipe only. What it made stays, and is listed under Creations.
  deleteWorkflow: (id) => request('DELETE', `/workflows/${id}`),
  // One finished image or clip, and the file behind it.
  deleteGeneration: (id) => request('DELETE', `/generations/${id}`),
  // compile & generate
  compile: (d) => request('POST', '/compile', d),
  generate: (d) => request('POST', '/generate', d),
  generateVideo: (d) => request('POST', '/generate-video', d),
  // Audio-driven avatar clip -- lip-synced to a supplied audio track, not an
  // animation of a still. Its own endpoint: generateVideo always starts from
  // a prior image generation, and this one's only required input is audio.
  generateAvatarVideo: (d) => request('POST', '/generate-avatar-video', d),
  generateSubtitles: (id) => request('POST', `/generations/${id}/subtitles`),
  voiceoverScript: (d) => request('POST', '/voiceover/script', d),
  voiceover: (d) => request('POST', '/voiceover', d),
  uploadAudio: (d) => request('POST', '/audio/upload', d),
  getGeneration: (id) => request('GET', `/generations/${id}`),
  listGenerations: (wid) => request('GET', `/generations${wid ? `?workflowId=${wid}` : ''}`),
  // Paged variant for Creations: the gallery loads 40 at a time instead of
  // mounting hundreds of <video> elements in one go.
  listGenerationsPage: (skip = 0, limit = 40) => request('GET', `/generations?skip=${skip}&limit=${limit}`),
  // catalog
  getCredits: () => request('GET', '/credits'),
  creditCatalogue: () => request('GET', '/credit-catalogue'),
  buyLink: (key) => request('GET', `/buy-link/${key}`),
  getStyleTemplates: (v) => request('GET', `/style-templates${v ? `?vertical=${v}` : ''}`),
  // influencers
  listInfluencers: () => request('GET', '/influencers'),
  getInfluencer: (id) => request('GET', `/influencers/${id}`),
  createInfluencer: (d) => request('POST', '/influencers', d),
  updateInfluencer: (id, d) => request('PUT', `/influencers/${id}`, d),
  deleteInfluencer: (id) => request('DELETE', `/influencers/${id}`),
  generateInfluencerFace: (d) => request('POST', '/influencers/generate-face', d),
  influencerConsistency: (id) => request('POST', `/influencers/${id}/consistency`),
  getGallery: (v) => request('GET', `/gallery${v ? `?vertical=${v}` : ''}`),
  // admin
  adminStats: () => request('GET', '/admin/stats'),
  adminUsers: () => request('GET', '/admin/users'),
  adminPatchUser: (id, d) => request('PATCH', `/admin/users/${id}`, d),
  adminDeleteUser: (id) => request('DELETE', `/admin/users/${id}`),
  adminTemplates: () => request('GET', '/admin/style-templates'),
  adminCreateTemplate: (d) => request('POST', '/admin/style-templates', d),
  adminPatchTemplate: (id, d) => request('PATCH', `/admin/style-templates/${id}`, d),
  adminDeleteTemplate: (id) => request('DELETE', `/admin/style-templates/${id}`),
  adminGallery: () => request('GET', '/admin/gallery'),
  adminCreateGallery: (d) => request('POST', '/admin/gallery', d),
  adminToggleGallery: (id, featured) => request('PATCH', `/admin/gallery/${id}`, { featured }),
  adminDeleteGallery: (id) => request('DELETE', `/admin/gallery/${id}`),
};
